/**
 * HudHTML - HTML overlay HUD for the ARPG game.
 * Sits on top of the game canvas permanently while playing.
 * Shows HP/Mana bars, skill hotbar, level/XP, quest tracker, and buff bar.
 */

import { Player } from '@client/entities/Player';
import { isSkillV2, HotbarSkill } from '@shared/skills/SkillCreator';
import { CustomSkillV2, SkillCreatorV2 } from '@shared/skills/SkillCreatorV2';
import { ElementType } from '@shared/types';
import { ActiveAuraInfo } from '@client/ui/HUD';
import { Minimap } from '@client/ui/Minimap';
// Renderer not directly used - Minimap.render() gets a fake renderer wrapper

// ========================
// Constants
// ========================

const ELEMENT_COLORS: Record<string, string> = {
  physical: '#dadada',
  od: '#da7e7e',
  su: '#7edada',
  gok: '#7eb8da',
  karanlik: '#9b59b6',
};

const SLOT_KEYS = ['1', '2', '3', '4', '5', '6'];

// ========================
// CSS (injected once)
// ========================

const CSS = `
.hud-root {
  position: fixed;
  top: 0; left: 0; right: 0; bottom: 0;
  z-index: 100;
  pointer-events: none;
  font-family: 'Segoe UI', Tahoma, sans-serif;
  color: #f4e8d4;
  user-select: none;
  overflow: hidden;
}

/* ---- Minimap (top-right) ---- */
.hud-minimap {
  position: absolute;
  top: 10px; right: 10px;
  width: 160px; height: 160px;
  border-radius: 8px;
  overflow: hidden;
  border: 2px solid #3a3530;
  box-shadow: 0 2px 12px rgba(0,0,0,0.8), inset 0 0 8px rgba(0,0,0,0.5);
  background: #0a0a14;
  pointer-events: auto;
  cursor: pointer;
  z-index: 4;
  transition: width 0.3s ease, height 0.3s ease;
}
.hud-minimap canvas {
  width: 100%;
  height: 100%;
  display: block;
}
.hud-minimap--expanded {
  width: min(60vw, 60vh);
  height: min(60vw, 60vh);
  top: 50%; left: 50%; right: auto;
  transform: translate(-50%, -50%);
  border-radius: 12px;
  border-color: #5a5040;
  z-index: 20;
}

/* ---- Dropdown Menu (top-left) ---- */
.hud-dropdown {
  position: absolute;
  top: 10px; left: 10px;
  pointer-events: auto;
  z-index: 5;
}
.hud-dropdown-btn {
  background: linear-gradient(180deg, #1a1510 0%, #0d0b08 100%);
  border: 1px solid #3a3530;
  border-radius: 6px;
  color: #f4e8d4;
  padding: 6px 14px;
  font-size: 14px;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 6px;
  font-weight: 600;
  text-shadow: 0 1px 2px #000;
}
.hud-dropdown-btn:hover {
  border-color: #5a5040;
  background: linear-gradient(180deg, #2a2520 0%, #151210 100%);
}
.hud-dropdown-list {
  display: none;
  position: absolute;
  top: 100%;
  left: 0;
  margin-top: 4px;
  background: linear-gradient(180deg, #1a1510 0%, #0d0b08 100%);
  border: 1px solid #3a3530;
  border-radius: 6px;
  min-width: 200px;
  overflow: hidden;
  box-shadow: 0 4px 16px rgba(0,0,0,0.8);
}
.hud-dropdown--open .hud-dropdown-list {
  display: block;
}
.hud-dropdown-item {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 8px 14px;
  cursor: pointer;
  font-size: 13px;
  color: #d4c8b4;
  border-bottom: 1px solid rgba(255,255,255,0.04);
  transition: background 0.15s;
}
.hud-dropdown-item:last-child { border-bottom: none; }
.hud-dropdown-item:hover {
  background: rgba(255,215,0,0.08);
  color: #ffd700;
}
.hud-dropdown-key {
  font-size: 11px;
  color: #888;
  font-weight: 600;
  background: rgba(255,255,255,0.06);
  padding: 2px 6px;
  border-radius: 3px;
}

/* ---- Quest Tracker (below minimap) ---- */
.hud-quest-wrap {
  position: absolute;
  top: 180px; right: 10px;
  max-width: clamp(160px, 18vw, 260px);
  pointer-events: auto;
  z-index: 3;
}
.hud-quest-toggle {
  background: linear-gradient(180deg, #1a1510, #0d0b08);
  border: 1px solid #3a3530;
  border-radius: 4px;
  color: #c8b070;
  padding: 3px 8px;
  font-size: 10px;
  cursor: pointer;
  margin-bottom: 4px;
  display: flex;
  align-items: center;
  gap: 4px;
  font-weight: 600;
  float: right;
}
.hud-quest-toggle:hover { border-color: #5a5040; color: #ffd700; }
.hud-quest {
  background: rgba(0,0,0,0.6);
  border-radius: 6px;
  padding: 8px 12px;
  font-size: clamp(11px, 1.1vw, 14px);
  border: 1px solid rgba(255,215,0,0.15);
  display: none;
  clear: both;
  backdrop-filter: blur(4px);
}
.hud-quest--visible { display: block; }
.hud-quest-title {
  color: #ffd700;
  font-weight: 600;
  margin-bottom: 4px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}
.hud-quest-obj {
  color: #ccc;
  line-height: 1.4;
}
.hud-quest-obj-done { color: #66cc66; text-decoration: line-through; }

/* ---- FPS / Locale info (bottom panel) ---- */
.hud-info {
  position: absolute;
  bottom: clamp(26px, 2.6vw, 36px);
  right: clamp(14px, 2vw, 28px);
  font-size: 10px;
  font-weight: 600;
  color: #555;
  text-shadow: 0 1px 2px rgba(0,0,0,0.8);
  pointer-events: none;
  z-index: 3;
}

/* ---- Bottom Section: POE-style panel ---- */
.hud-bottom {
  position: absolute;
  bottom: 0; left: 0; right: 0;
  height: clamp(110px, 11vw, 150px);
  pointer-events: none;
  display: flex;
  align-items: flex-end;
  justify-content: center;
}

/* Dark ornate panel background */
.hud-bottom-panel {
  position: absolute;
  bottom: 0; left: 0; right: 0;
  height: clamp(58px, 5.8vw, 78px);
  background: linear-gradient(180deg, #1c1814 0%, #12100c 40%, #0a0908 100%);
  border-top: 2px solid #4a4035;
  box-shadow: 0 -6px 24px rgba(0,0,0,0.9), inset 0 1px 0 rgba(255,255,255,0.04);
  pointer-events: auto;
}
.hud-bottom-panel::before {
  content: '';
  position: absolute;
  top: -1px; left: 0; right: 0;
  height: 1px;
  background: linear-gradient(90deg, transparent 5%, rgba(180,150,80,0.3) 25%, rgba(255,215,0,0.4) 50%, rgba(180,150,80,0.3) 75%, transparent 95%);
}
.hud-bottom-panel::after {
  content: '';
  position: absolute;
  top: 4px; left: 0; right: 0;
  height: 1px;
  background: linear-gradient(90deg, transparent 10%, rgba(255,255,255,0.03) 30%, rgba(255,255,255,0.05) 50%, rgba(255,255,255,0.03) 70%, transparent 90%);
}

/* Globe container - sits above panel */
.hud-resource {
  pointer-events: auto;
  display: flex;
  flex-direction: column;
  align-items: center;
  position: relative;
  z-index: 2;
  margin-bottom: clamp(6px, 0.6vw, 10px);
}

/* The globe orb */
.hud-globe {
  width: clamp(84px, 8.5vw, 116px);
  height: clamp(84px, 8.5vw, 116px);
  border-radius: 50%;
  background: radial-gradient(circle at 40% 35%, #1a1515, #0a0606 70%, #050303);
  border: 3px solid #4a4035;
  position: relative;
  overflow: hidden;
  box-shadow:
    inset 0 6px 20px rgba(0,0,0,0.95),
    inset 0 -3px 10px rgba(0,0,0,0.5),
    0 3px 12px rgba(0,0,0,0.9),
    0 0 20px rgba(0,0,0,0.6);
}

/* HP fill - rises from bottom with liquid depth */
.hud-hp-fill {
  position: absolute;
  bottom: 0; left: 0; right: 0;
  background:
    radial-gradient(ellipse at 35% 30%, rgba(255,120,80,0.3), transparent 60%),
    linear-gradient(0deg, #5a0000 0%, #aa1818 25%, #dd3333 50%, #ee5544 75%, #ff7755 95%);
  transition: height 0.3s ease;
  height: 100%;
}
/* Subtle animated shimmer on HP */
.hud-hp-fill::after {
  content: '';
  position: absolute;
  top: 0; left: -20%; right: -20%; bottom: 0;
  background: linear-gradient(105deg, transparent 40%, rgba(255,255,255,0.06) 45%, rgba(255,255,255,0.1) 50%, rgba(255,255,255,0.06) 55%, transparent 60%);
  pointer-events: none;
}

/* ES ring - thick blue arc on the right side of the globe, fills bottom to top */
.hud-es-ring {
  position: absolute;
  inset: -6px;
  border-radius: 50%;
  border: 6px solid transparent;
  border-right-color: #4ac0ff;
  filter: drop-shadow(0 0 6px rgba(70,170,255,0.7)) drop-shadow(0 0 12px rgba(40,120,220,0.4));
  z-index: 5;
  pointer-events: none;
  transition: clip-path 0.3s ease;
}

/* ES text label (shown near globe when ES > 0) */
.hud-es-text {
  position: absolute;
  top: 14%;
  left: 50%;
  transform: translateX(-50%);
  font-size: clamp(9px, 0.9vw, 11px);
  font-weight: 700;
  color: #6ec6ff;
  text-shadow: 0 1px 4px #000, 0 0 6px rgba(0,80,200,0.8);
  z-index: 6;
  white-space: nowrap;
}

/* Mana fill - rises from bottom with liquid depth */
.hud-mana-fill {
  position: absolute;
  bottom: 0; left: 0; right: 0;
  background:
    radial-gradient(ellipse at 35% 30%, rgba(100,180,255,0.25), transparent 60%),
    linear-gradient(0deg, #081840 0%, #1a44aa 25%, #3366dd 50%, #5599ff 75%, #77bbff 95%);
  transition: height 0.3s ease;
  height: 100%;
}
.hud-mana-fill::after {
  content: '';
  position: absolute;
  top: 0; left: -20%; right: -20%; bottom: 0;
  background: linear-gradient(105deg, transparent 40%, rgba(255,255,255,0.05) 45%, rgba(255,255,255,0.08) 50%, rgba(255,255,255,0.05) 55%, transparent 60%);
  pointer-events: none;
}

/* Mana reserved (darker section at top) */
.hud-mana-reserved {
  position: absolute;
  top: 0; left: 0; right: 0;
  background: rgba(0,0,20,0.65);
  border-bottom: 1px solid rgba(100,100,200,0.15);
  transition: height 0.3s ease;
  height: 0%;
}

/* Glass reflection - bright specular highlight */
.hud-globe::after {
  content: '';
  position: absolute;
  top: 6%; left: 12%; right: 45%;
  height: 22%;
  background: radial-gradient(ellipse at 35% 50%, rgba(255,255,255,0.25), rgba(255,255,255,0.08) 50%, transparent 75%);
  border-radius: 50%;
  pointer-events: none;
  z-index: 2;
}

/* Inner shadow for depth */
.hud-globe::before {
  content: '';
  position: absolute;
  inset: 0;
  border-radius: 50%;
  box-shadow: inset 0 0 20px rgba(0,0,0,0.5), inset 0 8px 16px rgba(0,0,0,0.3);
  z-index: 3;
  pointer-events: none;
}

/* Globe ornament ring - metallic frame */
.hud-globe-ring {
  position: absolute;
  inset: -7px;
  border-radius: 50%;
  border: 3px solid #3d3428;
  background: transparent;
  box-shadow:
    inset 0 1px 0 rgba(255,255,255,0.08),
    inset 0 -1px 0 rgba(0,0,0,0.3),
    0 0 8px rgba(0,0,0,0.6);
  pointer-events: none;
  z-index: 5;
}

/* Text overlay on globe */
.hud-bar-text {
  position: absolute;
  bottom: 18%;
  left: 50%;
  transform: translateX(-50%);
  font-size: clamp(12px, 1.2vw, 15px);
  font-weight: 700;
  color: #fff;
  text-shadow: 0 1px 3px #000, 0 0 6px rgba(0,0,0,0.9), 0 0 12px rgba(0,0,0,0.5);
  z-index: 4;
  white-space: nowrap;
  letter-spacing: 0.5px;
}

/* Potion slot - integrated pill below globe */
.hud-potion {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 4px;
  font-size: 10px;
  color: #b0a89a;
  pointer-events: auto;
  background: linear-gradient(180deg, #1a1714, #0e0c0a);
  padding: 3px 12px;
  border-radius: 0 0 10px 10px;
  border: 1px solid #3d3428;
  border-top: none;
  z-index: 10;
  margin-top: -2px;
  box-shadow: 0 2px 6px rgba(0,0,0,0.5);
  position: relative;
}
.hud-potion-key {
  background: rgba(255,215,0,0.08);
  border: 1px solid rgba(255,215,0,0.15);
  border-radius: 3px;
  padding: 1px 5px;
  font-weight: 700;
  font-size: 9px;
  color: #c8b070;
}

/* Gold display - inside bottom panel, right side */
.hud-gold {
  position: absolute;
  bottom: clamp(8px, 1vw, 14px);
  right: clamp(14px, 2vw, 28px);
  font-size: clamp(12px, 1.2vw, 15px);
  font-weight: 700;
  color: #f0d060;
  text-shadow: 0 0 8px rgba(255,200,0,0.3), 0 1px 3px rgba(0,0,0,0.9);
  pointer-events: none;
  z-index: 3;
}

/* Level badge - inside bottom panel, left side */
.hud-level-badge {
  position: absolute;
  bottom: clamp(8px, 1vw, 14px);
  left: clamp(14px, 2vw, 28px);
  font-size: clamp(12px, 1.2vw, 15px);
  font-weight: 700;
  color: #f0d060;
  text-shadow: 0 0 8px rgba(255,200,0,0.3), 0 1px 3px rgba(0,0,0,0.9);
  pointer-events: none;
  z-index: 3;
}

/* XP bar at very bottom spanning full width */
.hud-xp-bottom {
  position: absolute;
  bottom: 0; left: 0; right: 0;
  height: 5px;
  background: rgba(0,0,0,0.95);
  overflow: hidden;
  z-index: 6;
  border-top: 1px solid rgba(255,215,0,0.1);
}
.hud-xp-bottom-fill {
  height: 100%;
  background: linear-gradient(90deg, #7a5500, #d4a800 30%, #ffd700 60%, #ffe855 85%, #fff4aa);
  transition: width 0.5s ease;
  box-shadow: 0 0 8px rgba(255,215,0,0.5), 0 0 3px rgba(255,215,0,0.8);
}

/* Low HP pulse on globe */
@keyframes pulse-red {
  0%, 100% { box-shadow: inset 0 6px 20px rgba(0,0,0,0.95), 0 0 15px rgba(255,0,0,0.2); }
  50% { box-shadow: inset 0 6px 20px rgba(0,0,0,0.95), 0 0 30px rgba(255,0,0,0.6), 0 0 60px rgba(255,0,0,0.2); }
}
.hud-resource--low .hud-globe {
  animation: pulse-red 0.8s ease-in-out infinite;
  border-color: #773322;
}

/* ---- Skill Hotbar ---- */
.hud-hotbar {
  display: flex;
  gap: 4px;
  margin: 0 clamp(8px, 1.2vw, 18px);
  align-items: flex-end;
  margin-bottom: clamp(8px, 1vw, 14px);
  z-index: 2;
}
.hud-skill {
  width: clamp(42px, 4.2vw, 54px);
  height: clamp(42px, 4.2vw, 54px);
  background: linear-gradient(180deg, #1a1714 0%, #0c0a08 100%);
  border-radius: 5px;
  border: 2px solid #3d3428;
  position: relative;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  pointer-events: auto;
  cursor: default;
  overflow: hidden;
  box-shadow: inset 0 1px 0 rgba(255,255,255,0.04), 0 2px 6px rgba(0,0,0,0.5);
}
.hud-skill--empty {
  opacity: 0.4;
  border-color: #2a2420;
}
.hud-skill--active {
  border-color: #c8a840 !important;
  box-shadow: 0 0 12px rgba(255,215,0,0.4), inset 0 0 8px rgba(255,215,0,0.1);
}
.hud-skill-key {
  position: absolute;
  top: 2px; left: 3px;
  font-size: 9px;
  font-weight: 700;
  color: #c8b070;
  text-shadow: 0 1px 2px #000;
}
.hud-skill-dot {
  width: 6px; height: 6px;
  border-radius: 50%;
  margin-bottom: 2px;
}
.hud-skill-name {
  font-size: clamp(8px, 0.7vw, 10px);
  font-weight: 600;
  text-align: center;
  text-shadow: 0 0 2px #000;
  max-width: 100%;
  overflow: hidden;
  white-space: nowrap;
}
.hud-skill-cost {
  position: absolute;
  bottom: 1px;
  font-size: 8px;
  font-weight: 600;
  text-shadow: 0 0 2px #000;
}
.hud-skill-cost--ok { color: #6af; }
.hud-skill-cost--no { color: #f66; }

/* Cooldown overlay */
.hud-skill-cd {
  position: absolute;
  inset: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: clamp(12px, 1.2vw, 16px);
  font-weight: 700;
  color: #fff;
  text-shadow: 0 0 4px #000;
  pointer-events: none;
}
.hud-skill-cd-bg {
  position: absolute;
  inset: 0;
  border-radius: 4px;
}

/* ---- Buff / Debuff Bar ---- */
.hud-buffs {
  position: absolute;
  bottom: clamp(120px, 12vw, 160px);
  left: 50%;
  transform: translateX(-50%);
  display: flex;
  gap: 4px;
  flex-wrap: wrap;
  justify-content: center;
  max-width: 400px;
  pointer-events: none;
}
.hud-buff {
  width: 28px; height: 28px;
  border-radius: 5px;
  border: 1px solid rgba(255,255,255,0.25);
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 14px;
  position: relative;
  box-shadow: 0 1px 4px rgba(0,0,0,0.6);
  text-shadow: 0 1px 2px #000;
}
.hud-buff--debuff {
  border-color: rgba(255,60,60,0.5);
  box-shadow: 0 0 6px rgba(255,0,0,0.2), 0 1px 4px rgba(0,0,0,0.6);
}
.hud-buff-timer {
  position: absolute;
  bottom: -9px;
  font-size: 8px;
  font-weight: 600;
  color: #ddd;
  text-shadow: 0 0 3px #000;
  white-space: nowrap;
}

/* ---- Boss HP Bar ---- */
.hud-boss {
  position: absolute;
  top: 28px; left: 50%;
  transform: translateX(-50%);
  width: clamp(300px, 40vw, 520px);
  display: none;
  flex-direction: column;
  align-items: center;
  pointer-events: none;
  z-index: 3;
}
.hud-boss--active { display: flex; }
.hud-boss-name {
  font-size: clamp(14px, 1.5vw, 20px);
  font-weight: 700;
  color: #ff4444;
  text-shadow: 0 0 8px rgba(255,0,0,0.5), 0 1px 3px rgba(0,0,0,0.9);
  margin-bottom: 4px;
}
.hud-boss-bar-wrap {
  width: 100%;
  height: 20px;
  background: rgba(0,0,0,0.7);
  border-radius: 4px;
  overflow: hidden;
  border: 1px solid rgba(255,60,60,0.4);
  position: relative;
}
.hud-boss-fill {
  height: 100%;
  background: linear-gradient(90deg, #aa1111, #ff3333, #ff5555);
  border-radius: 3px;
  transition: width 0.4s ease;
  width: 100%;
}
.hud-boss-fill--low {
  background: linear-gradient(90deg, #cc6600, #ff8800, #ffaa22);
}
.hud-boss-text {
  position: absolute;
  top: 50%; left: 50%;
  transform: translate(-50%,-50%);
  font-size: 11px;
  font-weight: 600;
  color: #fff;
  text-shadow: 0 1px 2px #000;
}

/* ---- Aura Bar ---- */
.hud-aura-bar {
  display: flex;
  gap: 4px;
  justify-content: center;
  margin-bottom: 10px;
  pointer-events: auto;
}
.hud-aura {
  width: 28px; height: 28px;
  border-radius: 50%;
  border: 2px solid rgba(255,255,255,0.3);
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  font-size: 10px;
  cursor: pointer;
  position: relative;
  background: rgba(0,0,0,0.6);
}
.hud-aura:hover { border-color: rgba(255,255,255,0.7); }
.hud-aura-reserve {
  font-size: 7px;
  color: #6af;
  text-shadow: 0 0 2px #000;
  position: absolute;
  bottom: -9px;
  white-space: nowrap;
}

/* ---- Passive Point Badge (bottom panel, left side) ---- */
.hud-passive-badge {
  display: none;
  position: absolute;
  top: -36px;
  left: clamp(14px, 2vw, 28px);
  z-index: 7;
  pointer-events: auto;
  cursor: pointer;
  background: linear-gradient(180deg, #2a2210 0%, #1a1508 100%);
  border: 1px solid rgba(255,215,0,0.4);
  border-radius: 14px;
  padding: 4px 14px;
  font-size: clamp(10px, 1vw, 12px);
  font-weight: 700;
  color: #ffd700;
  text-shadow: 0 0 6px rgba(255,215,0,0.5), 0 1px 2px rgba(0,0,0,0.8);
  white-space: nowrap;
}
.hud-passive-badge:hover {
  background: linear-gradient(180deg, #3a3218 0%, #2a2210 100%);
  border-color: rgba(255,215,0,0.7);
  color: #ffe555;
}
.hud-passive-badge--visible { display: flex; align-items: center; gap: 5px; }
@keyframes pulse-gold {
  0%, 100% { box-shadow: 0 0 6px rgba(255,215,0,0.2); }
  50% { box-shadow: 0 0 16px rgba(255,215,0,0.6); }
}
.hud-passive-badge--visible {
  animation: pulse-gold 1.5s ease-in-out infinite;
}

/* ---- Combat Log ---- */
.hud-combat-log {
  position: absolute;
  bottom: clamp(110px, 11vw, 150px);
  left: 12px;
  display: flex;
  flex-direction: column;
  gap: 2px;
  pointer-events: none;
  max-width: 250px;
}
.hud-combat-entry {
  font-size: 11px;
  text-shadow: 0 1px 2px rgba(0,0,0,0.9);
  opacity: 1;
  transition: opacity 0.5s ease;
  white-space: nowrap;
}
.hud-combat-entry--fade { opacity: 0; }

/* ---- Pickup Notifications ---- */
.hud-pickups {
  position: absolute;
  top: 50%; right: 12px;
  transform: translateY(-50%);
  display: flex;
  flex-direction: column;
  gap: 4px;
  pointer-events: none;
  align-items: flex-end;
}
@keyframes slide-in-right {
  from { transform: translateX(100px); opacity: 0; }
  to { transform: translateX(0); opacity: 1; }
}
.hud-pickup {
  font-size: 13px;
  font-weight: 600;
  text-shadow: 0 1px 3px rgba(0,0,0,0.9);
  padding: 3px 10px;
  background: rgba(0,0,0,0.5);
  border-radius: 4px;
  animation: slide-in-right 0.3s ease-out;
  transition: opacity 0.5s ease;
  white-space: nowrap;
}
.hud-pickup--fade { opacity: 0; }

/* ---- Quest Complete Notification ---- */
.hud-quest-complete {
  position: absolute;
  top: 30%; left: 50%;
  transform: translate(-50%,-50%);
  display: none;
  flex-direction: column;
  align-items: center;
  pointer-events: none;
  z-index: 10;
}
.hud-quest-complete--active { display: flex; }
@keyframes quest-complete-in {
  0% { transform: translate(-50%,-50%) scale(0.5); opacity: 0; }
  30% { transform: translate(-50%,-50%) scale(1.1); opacity: 1; }
  100% { transform: translate(-50%,-50%) scale(1); opacity: 1; }
}
@keyframes quest-complete-out {
  from { opacity: 1; }
  to { opacity: 0; }
}
.hud-quest-complete--active {
  animation: quest-complete-in 0.6s ease-out;
}
.hud-quest-complete--fading {
  animation: quest-complete-out 1s ease-in forwards;
}
.hud-qc-title {
  font-size: clamp(22px, 3vw, 36px);
  font-weight: 700;
  color: #ffd700;
  text-shadow: 0 0 12px rgba(255,215,0,0.6), 0 2px 4px rgba(0,0,0,0.9);
}
.hud-qc-name {
  font-size: clamp(14px, 1.8vw, 22px);
  font-weight: 600;
  color: #f4e8d4;
  text-shadow: 0 1px 3px rgba(0,0,0,0.8);
  margin-top: 6px;
}
.hud-qc-rewards {
  font-size: clamp(11px, 1.2vw, 15px);
  color: #aaa;
  text-shadow: 0 1px 2px rgba(0,0,0,0.8);
  margin-top: 4px;
}
`;

let cssInjected = false;
function injectCSS(): void {
  if (cssInjected) return;
  const style = document.createElement('style');
  style.textContent = CSS;
  document.head.appendChild(style);
  cssInjected = true;
}

// ========================
// Tracked quest data (passed from outside)
// ========================

export interface TrackedQuest {
  name: string;
  objectives: { description: string; current: number; required: number; completed: boolean }[];
}

export interface MinimapEntity {
  x: number;
  y: number;
  isAlive: boolean;
}

export interface MinimapNPC {
  x: number;
  y: number;
}

export interface BossInfo {
  active: boolean;
  name?: string;
  hp?: number;
  maxHp?: number;
}

interface CombatLogEntry {
  text: string;
  color: string;
  el: HTMLDivElement;
  timer: number;
}

interface PickupEntry {
  el: HTMLDivElement;
  timer: number;
}

// ========================
// HudHTML Class
// ========================

export class HudHTML {
  private root: HTMLDivElement | null = null;
  private shown = false;

  // Element refs
  private elLevel!: HTMLElement;
  private elHpFill!: HTMLDivElement;
  private elHpText!: HTMLSpanElement;
  private elHpResource!: HTMLDivElement;
  private elEsFill!: HTMLDivElement;
  private elEsText!: HTMLSpanElement;
  private elManaFill!: HTMLDivElement;
  private elManaReserved!: HTMLDivElement;
  private elManaText!: HTMLSpanElement;
  private elHpPotionCount!: HTMLSpanElement;
  private elManaPotionCount!: HTMLSpanElement;
  private elQuestBox!: HTMLDivElement;
  private elQuestTitle!: HTMLDivElement;
  private elQuestObjs!: HTMLDivElement;
  private elQuestToggle!: HTMLButtonElement;
  private questVisible = true;
  private elBuffBar!: HTMLDivElement;
  private elInfo!: HTMLDivElement;
  private skillSlots: HTMLDivElement[] = [];
  private skillDots: HTMLDivElement[] = [];
  private skillNames: HTMLDivElement[] = [];
  private skillCosts: HTMLDivElement[] = [];
  private skillCdOverlays: HTMLDivElement[] = [];
  private skillCdTexts: HTMLDivElement[] = [];
  private skillCdBgs: HTMLDivElement[] = [];

  // Cached previous values (skip DOM writes when unchanged)
  private prevLevel = -1;
  private prevXpPct = -1;
  private elXpBottomFill!: HTMLDivElement;
  private prevHp = -1;
  private prevMaxHp = -1;
  private prevEs = -1;
  private prevMaxEs = -1;
  private prevMana = -1;
  private prevMaxMana = -1;
  private prevReservedPct = -1;
  private prevHpPotionCount = -1;
  private prevManaPotionCount = -1;
  private prevLowHp = false;
  private prevSkillIds: (string | null)[] = [null, null, null, null, null, null];
  private prevSkillCdPct: number[] = [0, 0, 0, 0, 0, 0];
  private prevSkillCdSec: string[] = ['', '', '', '', '', ''];
  private prevSkillCanAfford: boolean[] = [true, true, true, true, true, true];
  private prevSkillActive: boolean[] = [false, false, false, false, false, false];
  private prevQuestKey = '';
  private prevBuffKey = '';
  private fpsValue = 0;
  private localeValue = 'TR';
  private prevInfoText = '';

  // Quest data (set externally)
  private trackedQuest: TrackedQuest | null = null;
  // Minimap - uses the full Canvas Minimap renderer inside an HTML container
  private minimapCanvas!: HTMLCanvasElement;
  private minimapCtx!: CanvasRenderingContext2D;
  private minimapExpanded = false;
  private minimapLastUpdate = 0;
  private minimapEnemies: MinimapEntity[] = [];
  private minimapNPCs: MinimapNPC[] = [];
  private minimapWaypoints: { x: number; y: number }[] = [];
  private minimapExits: { x: number; y: number; label?: string }[] = [];
  private minimapWorldWidth = 1;
  private minimapWorldHeight = 1;
  private minimapOffsetX = 0;
  private canvasMinimap: Minimap | null = null; // Full Canvas minimap renderer
  // minimapRenderer removed - using fake renderer inline

  // Boss HP
  private elBoss!: HTMLDivElement;
  private elBossName!: HTMLDivElement;
  private elBossFill!: HTMLDivElement;
  private elBossText!: HTMLDivElement;
  private bossInfo: BossInfo = { active: false };
  private prevBossHp = -1;
  private prevBossMaxHp = -1;
  private prevBossName = '';
  private prevBossActive = false;

  // Gold
  private elGold!: HTMLDivElement;
  private prevGold = -1;

  // Aura bar
  private elAuraBar!: HTMLDivElement;
  // activeAuras synced from player in updateAuraBar
  private prevAuraKey = '';
  private auraToggleCallback: ((skillId: string) => void) | null = null;

  // Passive point badge
  private elPassiveBadge!: HTMLSpanElement;
  private prevPassiveBadgeText = '';
  private onOpenPassive: (() => void) | null = null;

  // Combat log
  private elCombatLog!: HTMLDivElement;
  private combatLogEntries: CombatLogEntry[] = [];

  // Pickup notifications
  private elPickups!: HTMLDivElement;
  private pickupEntries: PickupEntry[] = [];

  // Quest complete
  private elQuestComplete!: HTMLDivElement;
  private elQcTitle!: HTMLDivElement;
  private elQcName!: HTMLDivElement;
  private elQcRewards!: HTMLDivElement;
  private questCompleteTimer = 0;

  constructor() {
    injectCSS();
  }

  // ---- Public API ----

  show(): void {
    if (this.shown) return;
    this.build();
    document.body.appendChild(this.root!);
    this.shown = true;
  }

  hide(): void {
    if (!this.shown || !this.root) return;
    this.root.remove();
    this.shown = false;
  }

  isShown(): boolean {
    return this.shown;
  }

  setTrackedQuest(quest: TrackedQuest | null): void {
    this.trackedQuest = quest;
  }

  setFPS(fps: number): void {
    this.fpsValue = Math.round(fps);
  }

  setLocale(locale: string): void {
    this.localeValue = locale.toUpperCase();
  }

  setOpenPassiveCallback(cb: () => void): void {
    this.onOpenPassive = cb;
  }

  setBossInfo(info: BossInfo): void {
    this.bossInfo = info;
  }

  /** Toggle minimap expanded/small mode */
  toggleMinimap(): void {
    this.minimapExpanded = !this.minimapExpanded;
    const wrap = this.minimapCanvas?.parentElement;
    if (wrap) {
      if (this.minimapExpanded) {
        wrap.classList.add('hud-minimap--expanded');
        // Set canvas resolution to match expanded size
        const size = Math.min(window.innerWidth * 0.6, window.innerHeight * 0.6);
        this.minimapCanvas.width = Math.floor(size);
        this.minimapCanvas.height = Math.floor(size);
        // Update Canvas Minimap config for expanded
        if (this.canvasMinimap) {
          this.canvasMinimap.setExpanded(true);
          this.canvasMinimap.setScreenSize(Math.floor(size), Math.floor(size));
        }
      } else {
        wrap.classList.remove('hud-minimap--expanded');
        this.minimapCanvas.width = 150;
        this.minimapCanvas.height = 150;
        if (this.canvasMinimap) {
          this.canvasMinimap.setExpanded(false);
        }
      }
    }
    // Force immediate redraw
    this.minimapLastUpdate = 0;
  }

  /** Set the full Canvas Minimap instance for high-quality rendering */
  setCanvasMinimap(minimap: Minimap): void {
    this.canvasMinimap = minimap;
  }

  setEnemies(enemies: MinimapEntity[]): void {
    this.minimapEnemies = enemies;
  }

  setNPCs(npcs: MinimapNPC[]): void {
    this.minimapNPCs = npcs;
  }

  setWaypoints(waypoints: { x: number; y: number }[]): void {
    this.minimapWaypoints = waypoints;
  }

  setExits(exits: { x: number; y: number; label?: string }[]): void {
    this.minimapExits = exits;
  }

  setWorldDimensions(width: number, height: number, offsetX: number = 0): void {
    this.minimapWorldWidth = Math.max(1, width);
    this.minimapWorldHeight = Math.max(1, height);
    this.minimapOffsetX = offsetX;
  }

  setGold(amount: number): void {
    if (amount === this.prevGold) return;
    this.prevGold = amount;
    if (this.elGold) {
      this.elGold.textContent = `\u{1FA99} ${amount.toLocaleString()}`;
    }
  }

  setAuraToggleCallback(cb: (skillId: string) => void): void {
    this.auraToggleCallback = cb;
  }

  addCombatLog(text: string, color: string = '#ccc'): void {
    if (!this.shown || !this.elCombatLog) return;
    const el = document.createElement('div');
    el.className = 'hud-combat-entry';
    el.textContent = text;
    el.style.color = color;
    this.elCombatLog.appendChild(el);

    const entry: CombatLogEntry = { text, color, el, timer: 3.0 };
    this.combatLogEntries.push(entry);

    // Keep max 5
    while (this.combatLogEntries.length > 5) {
      const old = this.combatLogEntries.shift()!;
      old.el.remove();
    }
  }

  addPickupNotification(name: string, color: string): void {
    if (!this.shown || !this.elPickups) return;
    const el = document.createElement('div');
    el.className = 'hud-pickup';
    el.textContent = name;
    el.style.color = color;
    this.elPickups.appendChild(el);

    const entry: PickupEntry = { el, timer: 2.0 };
    this.pickupEntries.push(entry);
  }

  showQuestComplete(name: string, rewards?: string): void {
    if (!this.shown || !this.elQuestComplete) return;
    this.elQcTitle.textContent = 'Gorev Tamamlandi!';
    this.elQcName.textContent = name;
    this.elQcRewards.textContent = rewards || '';
    this.elQuestComplete.className = 'hud-quest-complete hud-quest-complete--active';
    this.questCompleteTimer = 3.0;
  }

  update(player: Player, deltaTime: number): void {
    if (!this.shown || !this.root) return;

    this.updateLevelBadge(player);
    this.updateHpBar(player);
    this.updateManaBar(player);
    this.updatePotions(player);
    this.updateSkillHotbar(player);
    this.updateQuestTracker();
    this.updateBuffBar(player);
    this.updateMinimap(player);
    this.updateBossBar();
    this.updateGold(player);
    this.updateAuraBar(player);
    this.updatePassiveBadge(player);
    this.updateCombatLog(deltaTime);
    this.updatePickups(deltaTime);
    this.updateQuestCompleteNotif(deltaTime);
    this.updateInfo();
  }

  // ---- Build DOM ----

  private build(): void {
    if (this.root) return;

    const root = document.createElement('div');
    root.className = 'hud-root';

    // Dropdown menu (top-left)
    root.appendChild(this.buildDropdownMenu());
    // Minimap (top-right)
    root.appendChild(this.buildMinimap());
    // Quest tracker (below minimap)
    root.appendChild(this.buildQuestTracker());
    // Buff bar
    root.appendChild(this.buildBuffBar());
    // Bottom section
    root.appendChild(this.buildBottom());
    // Boss bar
    root.appendChild(this.buildBossBar());
    // Combat log
    root.appendChild(this.buildCombatLog());
    // Pickup notifications
    root.appendChild(this.buildPickups());
    // Quest complete notification
    root.appendChild(this.buildQuestCompleteNotif());

    this.root = root;
  }

  private buildDropdownMenu(): HTMLDivElement {
    const wrap = document.createElement('div');
    wrap.className = 'hud-dropdown';

    const btn = document.createElement('button');
    btn.className = 'hud-dropdown-btn';
    btn.innerHTML = '\u2630 Menu';
    wrap.appendChild(btn);

    const list = document.createElement('div');
    list.className = 'hud-dropdown-list';

    const items: { label: string; key: string; code: string }[] = [
      { label: 'Envanter', key: 'I', code: 'KeyI' },
      { label: 'Pasif A\u011Fac\u0131', key: 'P', code: 'KeyP' },
      { label: 'Beceri Edit\u00F6r\u00FC', key: 'K', code: 'KeyK' },
      { label: 'Karakter', key: 'C', code: 'KeyC' },
      { label: 'G\u00F6revler', key: 'L', code: 'KeyL' },
      { label: 'Harita', key: 'TAB', code: 'Tab' },
      { label: 'Ayarlar', key: 'ESC', code: 'Escape' },
    ];

    for (const item of items) {
      const row = document.createElement('div');
      row.className = 'hud-dropdown-item';
      const nameSpan = document.createElement('span');
      nameSpan.textContent = item.label;
      const keySpan = document.createElement('span');
      keySpan.className = 'hud-dropdown-key';
      keySpan.textContent = item.key;
      row.appendChild(nameSpan);
      row.appendChild(keySpan);
      row.addEventListener('click', (e) => {
        e.stopPropagation();
        wrap.classList.remove('hud-dropdown--open');
        // Simulate keyboard event
        window.dispatchEvent(new KeyboardEvent('keydown', { code: item.code, bubbles: true }));
      });
      list.appendChild(row);
    }

    wrap.appendChild(list);

    btn.addEventListener('click', (e) => {
      e.stopPropagation();
      wrap.classList.toggle('hud-dropdown--open');
    });

    // Close dropdown when clicking outside
    document.addEventListener('click', () => {
      wrap.classList.remove('hud-dropdown--open');
    });

    return wrap;
  }

  private buildQuestTracker(): HTMLDivElement {
    const wrap = document.createElement('div');
    wrap.className = 'hud-quest-wrap';

    // Toggle button
    const toggle = document.createElement('button');
    toggle.className = 'hud-quest-toggle';
    toggle.textContent = '\u25BC G\u00F6rev';
    toggle.addEventListener('click', () => {
      this.questVisible = !this.questVisible;
      box.classList.toggle('hud-quest--visible', this.questVisible && !!this.trackedQuest);
      toggle.textContent = this.questVisible ? '\u25BC G\u00F6rev' : '\u25B6 G\u00F6rev';
    });
    wrap.appendChild(toggle);
    this.elQuestToggle = toggle;

    const box = document.createElement('div');
    box.className = 'hud-quest';
    this.elQuestTitle = document.createElement('div');
    this.elQuestTitle.className = 'hud-quest-title';
    this.elQuestObjs = document.createElement('div');
    this.elQuestObjs.className = 'hud-quest-obj';
    box.appendChild(this.elQuestTitle);
    box.appendChild(this.elQuestObjs);
    wrap.appendChild(box);

    this.elQuestBox = box;
    return wrap;
  }

  private buildBuffBar(): HTMLDivElement {
    const bar = document.createElement('div');
    bar.className = 'hud-buffs';
    this.elBuffBar = bar;
    return bar;
  }

  private buildBottom(): HTMLDivElement {
    const bottom = document.createElement('div');
    bottom.className = 'hud-bottom';

    // Dark panel background
    const panel = document.createElement('div');
    panel.className = 'hud-bottom-panel';
    bottom.appendChild(panel);

    // HP globe (left)
    bottom.appendChild(this.buildResource('hp'));

    // Center column: aura bar + skill hotbar
    const centerCol = document.createElement('div');
    centerCol.style.cssText = 'display:flex;flex-direction:column;align-items:center;gap:2px;z-index:2;margin-bottom:' + 'clamp(4px, 0.5vw, 8px);';
    this.elAuraBar = document.createElement('div');
    this.elAuraBar.className = 'hud-aura-bar';
    centerCol.appendChild(this.elAuraBar);
    centerCol.appendChild(this.buildHotbar());
    bottom.appendChild(centerCol);

    // Mana globe (right)
    bottom.appendChild(this.buildResource('mana'));

    // Level badge (inside panel, bottom-left)
    this.elLevel = document.createElement('div');
    this.elLevel.className = 'hud-level-badge';
    this.elLevel.textContent = 'Lv.1';
    bottom.appendChild(this.elLevel);

    // Passive point badge (clickable, opens passive tree) - on panel
    this.elPassiveBadge = document.createElement('span');
    this.elPassiveBadge.className = 'hud-passive-badge';
    this.elPassiveBadge.addEventListener('click', () => {
      if (this.onOpenPassive) this.onOpenPassive();
    });
    panel.appendChild(this.elPassiveBadge);

    // FPS / Locale info (above gold, bottom-right)
    this.elInfo = document.createElement('div');
    this.elInfo.className = 'hud-info';
    this.elInfo.textContent = 'FPS: 0 | TR';
    bottom.appendChild(this.elInfo);

    // Gold display (inside panel, bottom-right)
    this.elGold = document.createElement('div');
    this.elGold.className = 'hud-gold';
    this.elGold.textContent = '\u{1FA99} 0';
    bottom.appendChild(this.elGold);

    // XP bar at very bottom spanning full width
    const xpBottom = document.createElement('div');
    xpBottom.className = 'hud-xp-bottom';
    const xpFill = document.createElement('div');
    xpFill.className = 'hud-xp-bottom-fill';
    xpBottom.appendChild(xpFill);
    this.elXpBottomFill = xpFill;
    bottom.appendChild(xpBottom);

    return bottom;
  }

  private buildResource(type: 'hp' | 'mana'): HTMLDivElement {
    const wrap = document.createElement('div');
    wrap.className = 'hud-resource';

    const globeWrap = document.createElement('div');
    globeWrap.style.cssText = 'position:relative;';

    const globe = document.createElement('div');
    globe.className = 'hud-globe';

    const fill = document.createElement('div');
    fill.className = type === 'hp' ? 'hud-hp-fill' : 'hud-mana-fill';
    globe.appendChild(fill);

    if (type === 'hp') {
      // ES ring goes on globeWrap (outside the clipped globe)
      const esRing = document.createElement('div');
      esRing.className = 'hud-es-ring';
      esRing.style.clipPath = 'inset(100% 0 0 0)'; // hidden by default
      globeWrap.appendChild(esRing);
      this.elEsFill = esRing;

      const esText = document.createElement('span');
      esText.className = 'hud-es-text';
      esText.style.display = 'none';
      globe.appendChild(esText);
      this.elEsText = esText;
    }

    if (type === 'mana') {
      const reserved = document.createElement('div');
      reserved.className = 'hud-mana-reserved';
      reserved.style.height = '0%';
      globe.appendChild(reserved);
      this.elManaReserved = reserved;
    }

    const text = document.createElement('span');
    text.className = 'hud-bar-text';
    globe.appendChild(text);

    globeWrap.appendChild(globe);

    // Ornament ring around globe
    const ring = document.createElement('div');
    ring.className = 'hud-globe-ring';
    globeWrap.appendChild(ring);

    wrap.appendChild(globeWrap);

    // Potion
    const potion = document.createElement('div');
    potion.className = 'hud-potion';
    const key = document.createElement('span');
    key.className = 'hud-potion-key';
    key.textContent = type === 'hp' ? 'Q' : 'E';
    const count = document.createElement('span');
    potion.appendChild(key);
    potion.appendChild(count);
    wrap.appendChild(potion);

    if (type === 'hp') {
      this.elHpFill = fill;
      this.elHpText = text;
      this.elHpResource = wrap;
      this.elHpPotionCount = count;
    } else {
      this.elManaFill = fill;
      this.elManaText = text;
      this.elManaPotionCount = count;
    }

    return wrap;
  }

  private buildHotbar(): HTMLDivElement {
    const bar = document.createElement('div');
    bar.className = 'hud-hotbar';

    for (let i = 0; i < 6; i++) {
      const slot = document.createElement('div');
      slot.className = 'hud-skill hud-skill--empty';

      // Keybind
      const keyLabel = document.createElement('div');
      keyLabel.className = 'hud-skill-key';
      keyLabel.textContent = SLOT_KEYS[i];
      slot.appendChild(keyLabel);

      // Element dot
      const dot = document.createElement('div');
      dot.className = 'hud-skill-dot';
      dot.style.display = 'none';
      slot.appendChild(dot);

      // Name
      const name = document.createElement('div');
      name.className = 'hud-skill-name';
      slot.appendChild(name);

      // Mana cost
      const cost = document.createElement('div');
      cost.className = 'hud-skill-cost hud-skill-cost--ok';
      slot.appendChild(cost);

      // Cooldown bg
      const cdBg = document.createElement('div');
      cdBg.className = 'hud-skill-cd-bg';
      cdBg.style.display = 'none';
      slot.appendChild(cdBg);

      // Cooldown text
      const cdOverlay = document.createElement('div');
      cdOverlay.className = 'hud-skill-cd';
      cdOverlay.style.display = 'none';
      const cdText = document.createElement('div');
      cdOverlay.appendChild(cdText);
      slot.appendChild(cdOverlay);

      bar.appendChild(slot);

      this.skillSlots[i] = slot;
      this.skillDots[i] = dot;
      this.skillNames[i] = name;
      this.skillCosts[i] = cost;
      this.skillCdBgs[i] = cdBg;
      this.skillCdOverlays[i] = cdOverlay;
      this.skillCdTexts[i] = cdText;
    }

    return bar;
  }

  // ---- Update Helpers ----

  private updateLevelBadge(player: Player): void {
    // Level
    if (player.level !== this.prevLevel) {
      this.prevLevel = player.level;
      this.elLevel.textContent = `Lv.${player.level}`;
    }

    // XP (bottom bar only)
    const xpPct = Math.round(player.getExperiencePercent() * 100);
    if (xpPct !== this.prevXpPct) {
      this.prevXpPct = xpPct;
      if (this.elXpBottomFill) this.elXpBottomFill.style.width = `${xpPct}%`;
    }
  }

  private updateInfo(): void {
    const text = `FPS: ${this.fpsValue} | ${this.localeValue}`;
    if (text !== this.prevInfoText) {
      this.prevInfoText = text;
      this.elInfo.textContent = text;
    }
  }

  private updateHpBar(player: Player): void {
    const hp = Math.floor(player.stats.hp);
    const maxHp = Math.floor(player.stats.maxHp);

    if (hp !== this.prevHp || maxHp !== this.prevMaxHp) {
      this.prevHp = hp;
      this.prevMaxHp = maxHp;
      const pct = maxHp > 0 ? Math.min(100, Math.max(0, (hp / maxHp) * 100)) : 0;
      this.elHpFill.style.height = `${pct}%`;
      this.elHpText.textContent = `${hp}/${maxHp}`;
    }

    // Energy Shield ring on HP globe (blue border-right, fills bottom to top via clip-path)
    const es = Math.floor(player.stats.energyShield);
    const maxEs = Math.floor(player.stats.maxEnergyShield);
    if (es !== this.prevEs || maxEs !== this.prevMaxEs) {
      this.prevEs = es;
      this.prevMaxEs = maxEs;
      if (maxEs > 0 && es > 0) {
        const esPct = Math.min(100, Math.max(0, (es / maxEs) * 100));
        // clip-path inset: top right bottom left — reveal from bottom up
        const topClip = 100 - esPct;
        this.elEsFill.style.clipPath = `inset(${topClip}% 0 0 0)`;
        this.elEsText.textContent = `${es}`;
        this.elEsText.style.display = '';
      } else {
        this.elEsFill.style.clipPath = 'inset(100% 0 0 0)';
        this.elEsText.style.display = 'none';
      }
    }

    // Low HP warning
    const low = maxHp > 0 && hp / maxHp < 0.3;
    if (low !== this.prevLowHp) {
      this.prevLowHp = low;
      if (low) {
        this.elHpResource.classList.add('hud-resource--low');
      } else {
        this.elHpResource.classList.remove('hud-resource--low');
      }
    }
  }

  private updateManaBar(player: Player): void {
    const mana = Math.floor(player.stats.mana);
    const effectiveMax = Math.floor(player.getEffectiveMaxMana());

    if (mana !== this.prevMana || effectiveMax !== this.prevMaxMana) {
      this.prevMana = mana;
      this.prevMaxMana = effectiveMax;
      const pct = effectiveMax > 0 ? Math.min(100, Math.max(0, (mana / effectiveMax) * 100)) : 0;
      this.elManaFill.style.height = `${pct}%`;
      this.elManaText.textContent = `${mana}/${effectiveMax}`;
    }

    // Reserved mana section
    const totalReservedPct = player.stats.maxMana > 0
      ? Math.min(100, ((player.reservedMana / 100) * player.stats.maxMana + player.reservedManaFlat) / player.stats.maxMana * 100)
      : 0;
    const reservedRounded = Math.round(totalReservedPct);
    if (reservedRounded !== this.prevReservedPct) {
      this.prevReservedPct = reservedRounded;
      this.elManaReserved.style.height = reservedRounded > 0 ? `${reservedRounded}%` : '0%';
    }
  }

  private updatePotions(player: Player): void {
    const hpSlot = player.potionBelt.slots[0];
    const hpCount = hpSlot ? hpSlot.count : 0;
    if (hpCount !== this.prevHpPotionCount) {
      this.prevHpPotionCount = hpCount;
      this.elHpPotionCount.textContent = `HP x${hpCount}`;
    }

    const mpSlot = player.potionBelt.slots[1];
    const mpCount = mpSlot ? mpSlot.count : 0;
    if (mpCount !== this.prevManaPotionCount) {
      this.prevManaPotionCount = mpCount;
      this.elManaPotionCount.textContent = `MP x${mpCount}`;
    }
  }

  private updateSkillHotbar(player: Player): void {
    const currentMana = player.stats.mana;

    for (let i = 0; i < 6; i++) {
      const skill = player.skillHotbar.get(i);
      const skillId = skill ? skill.id : null;

      // Skill identity changed - rebuild slot content
      if (skillId !== this.prevSkillIds[i]) {
        this.prevSkillIds[i] = skillId;
        this.rebuildSkillSlot(i, skill);
      }

      if (!skill) continue;

      // Cooldown
      const cdPct = this.getSkillCooldownPct(skill);
      const cdPctRounded = Math.round(cdPct * 100);

      if (cdPctRounded !== this.prevSkillCdPct[i]) {
        this.prevSkillCdPct[i] = cdPctRounded;

        if (cdPctRounded > 0) {
          this.skillCdOverlays[i].style.display = '';
          this.skillCdBgs[i].style.display = '';
          // Conic gradient for clock-wipe
          const deg = Math.round(cdPct * 360);
          this.skillCdBgs[i].style.background =
            `conic-gradient(rgba(0,0,0,0.7) ${deg}deg, transparent ${deg}deg)`;
        } else {
          this.skillCdOverlays[i].style.display = 'none';
          this.skillCdBgs[i].style.display = 'none';
        }
      }

      // Cooldown seconds text
      if (cdPct > 0) {
        const sec = this.getSkillCooldownSec(skill);
        if (sec !== this.prevSkillCdSec[i]) {
          this.prevSkillCdSec[i] = sec;
          this.skillCdTexts[i].textContent = sec;
        }
      } else if (this.prevSkillCdSec[i] !== '') {
        this.prevSkillCdSec[i] = '';
        this.skillCdTexts[i].textContent = '';
      }

      // Can afford
      const canAfford = currentMana >= skill.manaCost;
      if (canAfford !== this.prevSkillCanAfford[i]) {
        this.prevSkillCanAfford[i] = canAfford;
        this.skillCosts[i].className = canAfford
          ? 'hud-skill-cost hud-skill-cost--ok'
          : 'hud-skill-cost hud-skill-cost--no';
      }

      // Active aura highlight
      const isActive = isSkillV2(skill)
        && (skill as CustomSkillV2).deliveryType === 'aura'
        && player.activeAuras.has(skill.id);
      if (isActive !== this.prevSkillActive[i]) {
        this.prevSkillActive[i] = isActive;
        if (isActive) {
          this.skillSlots[i].classList.add('hud-skill--active');
        } else {
          this.skillSlots[i].classList.remove('hud-skill--active');
        }
      }
    }
  }

  private rebuildSkillSlot(index: number, skill: HotbarSkill | null): void {
    const slot = this.skillSlots[index];

    if (!skill) {
      slot.className = 'hud-skill hud-skill--empty';
      this.skillDots[index].style.display = 'none';
      this.skillNames[index].textContent = '';
      this.skillCosts[index].textContent = '';
      this.skillCdOverlays[index].style.display = 'none';
      this.skillCdBgs[index].style.display = 'none';
      this.prevSkillCdPct[index] = 0;
      this.prevSkillCdSec[index] = '';
      this.prevSkillCanAfford[index] = true;
      this.prevSkillActive[index] = false;
      return;
    }

    slot.className = 'hud-skill';
    const element: ElementType = skill.element;
    const color = ELEMENT_COLORS[element] || '#aaa';

    // Border color from element
    slot.style.borderColor = color;

    // Dot
    this.skillDots[index].style.display = '';
    this.skillDots[index].style.backgroundColor = color;

    // Name (first 5 chars)
    this.skillNames[index].textContent = skill.name.length > 5
      ? skill.name.substring(0, 5)
      : skill.name;

    // Mana cost
    this.skillCosts[index].textContent = `${Math.round(skill.manaCost)}`;
  }

  private getSkillCooldownPct(skill: HotbarSkill): number {
    if (skill.cooldown <= 0) return 0;
    return Math.max(0, Math.min(1, skill.currentCooldown / skill.cooldown));
  }

  private getSkillCooldownSec(skill: HotbarSkill): string {
    const sec = skill.currentCooldown;
    if (sec <= 0) return '';
    return sec >= 10 ? Math.floor(sec).toString() : sec.toFixed(1);
  }

  private updateQuestTracker(): void {
    const quest = this.trackedQuest;
    if (!quest) {
      if (this.prevQuestKey !== '') {
        this.prevQuestKey = '';
        this.elQuestBox.classList.remove('hud-quest--visible');
        this.elQuestToggle.style.display = 'none';
      }
      return;
    }

    this.elQuestToggle.style.display = '';

    // Build a key to detect changes
    let key = quest.name;
    for (const o of quest.objectives) {
      key += `|${o.description}:${o.current}/${o.required}:${o.completed ? 1 : 0}`;
    }

    if (key === this.prevQuestKey) return;
    this.prevQuestKey = key;

    this.elQuestBox.classList.toggle('hud-quest--visible', this.questVisible);
    this.elQuestTitle.textContent = quest.name;

    // Build objectives
    let html = '';
    for (const o of quest.objectives) {
      const cls = o.completed ? 'hud-quest-obj-done' : '';
      const progress = o.required > 1 ? ` (${o.current}/${o.required})` : '';
      html += `<div class="${cls}">${o.description}${progress}</div>`;
    }
    this.elQuestObjs.innerHTML = html;
  }

  private updateBuffBar(player: Player): void {
    // Sync from player's status effects + buff manager
    const items: { id: string; name: string; icon: string; isDebuff: boolean; remainingTime: number; stacks: number; color: string }[] = [];

    // Status effects (bleed, poison, burn, chill, freeze, shock)
    const seIcons: Record<string, { icon: string; color: string }> = {
      bleed: { icon: '\u2666', color: '#880000' },
      poison: { icon: '\u2620', color: '#228800' },
      burn: { icon: '\u2668', color: '#CC4400' },
      chill: { icon: '\u2744', color: '#4488CC' },
      freeze: { icon: '\u2726', color: '#88CCFF' },
      shock: { icon: '\u26A1', color: '#CCCC00' },
    };
    for (const se of player.statusEffects.getAll()) {
      const info = seIcons[se.type];
      if (!info) continue;
      items.push({ id: `se_${se.type}`, name: se.type, icon: info.icon, isDebuff: true, remainingTime: se.remaining, stacks: se.stacks, color: info.color });
    }

    // Buff manager entries
    const buffIcons: Record<string, { icon: string; color: string }> = {
      damage_boost: { icon: '\u2191', color: '#884422' },
      crit_boost: { icon: '\u2605', color: '#AAAA22' },
      attack_speed: { icon: '\u21BB', color: '#448844' },
      cast_speed: { icon: '\u21BB', color: '#446688' },
      armor_boost: { icon: '\u25C6', color: '#666666' },
      damage_reduction: { icon: '\u25B2', color: '#4488FF' },
      shield: { icon: '\u25A0', color: '#8844FF' },
      invulnerable: { icon: '\u2729', color: '#FFD700' },
      speed_boost: { icon: '\u2192', color: '#448844' },
      hp_regen: { icon: '\u2665', color: '#228822' },
      mana_regen: { icon: '\u2727', color: '#224488' },
      leech_boost: { icon: '\u25C9', color: '#882244' },
      weakness: { icon: '\u2193', color: '#666666' },
      vulnerability: { icon: '\u25BD', color: '#FF0066' },
      curse: { icon: '\u2620', color: '#660066' },
      silence: { icon: '\u2715', color: '#990099' },
      blind: { icon: '\u25CF', color: '#333333' },
    };
    for (const buff of player.buffManager.getActiveBuffs()) {
      const info = buffIcons[buff.type] || { icon: '?', color: '#888' };
      items.push({ id: `buff_${buff.id}`, name: buff.name, icon: info.icon, isDebuff: buff.isDebuff, remainingTime: buff.remaining, stacks: buff.stacks, color: buff.color || info.color });
    }

    // Build a key to detect changes
    let key = '';
    for (const b of items) {
      key += `${b.id}:${Math.floor(b.remainingTime * 10)}:${b.stacks}|`;
    }
    if (key === this.prevBuffKey) return;
    this.prevBuffKey = key;

    this.elBuffBar.innerHTML = '';
    for (const b of items) {
      const el = document.createElement('div');
      el.className = b.isDebuff ? 'hud-buff hud-buff--debuff' : 'hud-buff';
      el.style.backgroundColor = b.color;
      el.textContent = b.icon;
      el.title = b.name;

      if (b.remainingTime > 0 && b.remainingTime < 999) {
        const timer = document.createElement('div');
        timer.className = 'hud-buff-timer';
        timer.textContent = b.remainingTime >= 10
          ? `${Math.floor(b.remainingTime)}s`
          : `${b.remainingTime.toFixed(1)}`;
        el.appendChild(timer);
      }

      if (b.stacks > 1) {
        const stack = document.createElement('div');
        stack.style.cssText = 'position:absolute;top:-2px;right:-2px;font-size:8px;font-weight:700;color:#ffd700;text-shadow:0 0 2px #000;';
        stack.textContent = `${b.stacks}`;
        el.appendChild(stack);
      }

      this.elBuffBar.appendChild(el);
    }
  }

  // ---- Minimap ----

  private buildMinimap(): HTMLDivElement {
    const wrap = document.createElement('div');
    wrap.className = 'hud-minimap';

    const canvas = document.createElement('canvas');
    canvas.width = 150;
    canvas.height = 150;
    wrap.appendChild(canvas);

    this.minimapCanvas = canvas;
    this.minimapCtx = canvas.getContext('2d')!;
    // Click to toggle expanded
    wrap.addEventListener('click', () => {
      this.minimapExpanded = !this.minimapExpanded;
      if (this.minimapExpanded) {
        wrap.classList.add('hud-minimap--expanded');
        canvas.width = 300;
        canvas.height = 300;
      } else {
        wrap.classList.remove('hud-minimap--expanded');
        canvas.width = 150;
        canvas.height = 150;
      }
      this.minimapLastUpdate = 0; // force redraw
    });

    return wrap;
  }

  private updateMinimap(player: Player): void {
    const now = performance.now();
    if (now - this.minimapLastUpdate < 100) return; // throttle to ~10fps
    this.minimapLastUpdate = now;

    // Use full Canvas Minimap if available (terrain, proper isometric projection)
    if (this.canvasMinimap) {
      const w = this.minimapCanvas.width;
      const h = this.minimapCanvas.height;
      this.canvasMinimap.setConfigSize(w);
      this.canvasMinimap.setPosition(0, 0);
      this.canvasMinimap.setScreenSize(w, h);
      const fakeRenderer = { context: this.minimapCtx, width: w, height: h } as any;
      this.canvasMinimap.updateEnemies(this.minimapEnemies.map(e => ({ position: { x: e.x, y: e.y }, isAlive: e.isAlive })) as any[]);

      // Feed NPC, waypoint and zone exit markers
      const markers: import('@client/ui/Minimap').MinimapMarker[] = [];
      for (const npc of this.minimapNPCs) {
        markers.push({ x: npc.x, y: npc.y, type: 'npc', label: '' });
      }
      for (const wp of this.minimapWaypoints) {
        markers.push({ x: wp.x, y: wp.y, type: 'waypoint', label: '' });
      }
      for (const exit of this.minimapExits) {
        markers.push({ x: exit.x, y: exit.y, type: 'portal', label: exit.label || '' });
      }
      this.canvasMinimap.setMarkers(markers);

      this.canvasMinimap.render(fakeRenderer);
      return;
    }

    // Fallback: simple dot-based minimap
    const ctx = this.minimapCtx;
    const w = this.minimapCanvas.width;
    const h = this.minimapCanvas.height;

    // Clear
    ctx.fillStyle = 'rgba(10,10,20,0.95)';
    ctx.fillRect(0, 0, w, h);

    const worldW = this.minimapWorldWidth;
    const worldH = this.minimapWorldHeight;
    const offX = this.minimapOffsetX;
    const px = player.position.x;
    const py = player.position.y;

    // Scale: map world coords to minimap coords (offset X so left edge is 0)
    const scaleX = w / worldW;
    const scaleY = h / worldH;

    // Draw enemies (red dots)
    ctx.fillStyle = '#ff4444';
    for (const e of this.minimapEnemies) {
      if (!e.isAlive) continue;
      const mx = (e.x + offX) * scaleX;
      const my = e.y * scaleY;
      ctx.fillRect(mx - 1, my - 1, 3, 3);
    }

    // Draw NPCs (yellow dots)
    ctx.fillStyle = '#ffdd44';
    for (const npc of this.minimapNPCs) {
      const mx = (npc.x + offX) * scaleX;
      const my = npc.y * scaleY;
      ctx.fillRect(mx - 2, my - 2, 4, 4);
    }

    // Draw waypoints (blue diamonds)
    ctx.fillStyle = '#4488ff';
    for (const wp of this.minimapWaypoints) {
      const mx = (wp.x + offX) * scaleX;
      const my = wp.y * scaleY;
      ctx.save();
      ctx.translate(mx, my);
      ctx.rotate(Math.PI / 4);
      ctx.fillRect(-3, -3, 6, 6);
      ctx.restore();
    }

    // Draw player (white dot, larger)
    const pmx = (px + offX) * scaleX;
    const pmy = py * scaleY;
    ctx.fillStyle = '#ffffff';
    ctx.beginPath();
    ctx.arc(pmx, pmy, 3, 0, Math.PI * 2);
    ctx.fill();

    // Border glow on player dot
    ctx.strokeStyle = 'rgba(255,255,255,0.4)';
    ctx.lineWidth = 1;
    ctx.beginPath();
    ctx.arc(pmx, pmy, 5, 0, Math.PI * 2);
    ctx.stroke();
  }

  // ---- Boss Bar ----

  private buildBossBar(): HTMLDivElement {
    const wrap = document.createElement('div');
    wrap.className = 'hud-boss';

    const name = document.createElement('div');
    name.className = 'hud-boss-name';
    wrap.appendChild(name);

    const barWrap = document.createElement('div');
    barWrap.className = 'hud-boss-bar-wrap';

    const fill = document.createElement('div');
    fill.className = 'hud-boss-fill';
    barWrap.appendChild(fill);

    const text = document.createElement('div');
    text.className = 'hud-boss-text';
    barWrap.appendChild(text);

    wrap.appendChild(barWrap);

    this.elBoss = wrap;
    this.elBossName = name;
    this.elBossFill = fill;
    this.elBossText = text;

    return wrap;
  }

  private updateBossBar(): void {
    const info = this.bossInfo;

    // Toggle visibility
    if (info.active !== this.prevBossActive) {
      this.prevBossActive = info.active;
      if (info.active) {
        this.elBoss.classList.add('hud-boss--active');
      } else {
        this.elBoss.classList.remove('hud-boss--active');
      }
    }

    if (!info.active) return;

    // Name
    const name = info.name || 'Boss';
    if (name !== this.prevBossName) {
      this.prevBossName = name;
      this.elBossName.textContent = name;
    }

    // HP bar
    const hp = Math.floor(info.hp || 0);
    const maxHp = Math.floor(info.maxHp || 1);
    if (hp !== this.prevBossHp || maxHp !== this.prevBossMaxHp) {
      this.prevBossHp = hp;
      this.prevBossMaxHp = maxHp;
      const pct = maxHp > 0 ? Math.min(100, Math.max(0, (hp / maxHp) * 100)) : 0;
      this.elBossFill.style.width = `${pct}%`;
      this.elBossText.textContent = `${hp} / ${maxHp}`;

      // Phase indicator: change color at low HP
      if (pct < 30) {
        this.elBossFill.classList.add('hud-boss-fill--low');
      } else {
        this.elBossFill.classList.remove('hud-boss-fill--low');
      }
    }
  }

  // ---- Gold ----

  private updateGold(player: Player): void {
    const gold = player.getGold();
    if (gold !== this.prevGold) {
      this.prevGold = gold;
      this.elGold.textContent = `\u{1FA99} ${gold.toLocaleString()}`;
    }
  }

  // ---- Aura Bar ----

  private updateAuraBar(player: Player): void {
    // Build aura list from player's active auras
    const auraInfos: ActiveAuraInfo[] = [];
    for (const skillId of player.activeAuras) {
      const skill = player.customSkillsV2.find(s => s.id === skillId);
      if (!skill || skill.deliveryType !== 'aura') continue;
      const effectStrength = SkillCreatorV2.getEffectStrength(skill);
      auraInfos.push({
        skillId: skill.id,
        name: skill.name,
        element: skill.element,
        effectStrength,
        manaReservePercent: skill.manaReservePercent || 0,
        damageBonus: effectStrength - 1.0,
      });
    }

    let key = '';
    for (const a of auraInfos) {
      key += `${a.skillId}:${a.manaReservePercent}|`;
    }

    if (key === this.prevAuraKey) return;
    this.prevAuraKey = key;

    this.elAuraBar.innerHTML = '';

    for (const a of auraInfos) {
      const el = document.createElement('div');
      el.className = 'hud-aura';
      const color = ELEMENT_COLORS[a.element] || '#aaa';
      el.style.borderColor = color;

      // Element dot
      const dot = document.createElement('div');
      dot.style.cssText = `width:8px;height:8px;border-radius:50%;background:${color};`;
      el.appendChild(dot);

      // Reserve %
      const reserve = document.createElement('div');
      reserve.className = 'hud-aura-reserve';
      reserve.textContent = `${Math.round(a.manaReservePercent)}%`;
      el.appendChild(reserve);

      el.title = `${a.name} (${Math.round(a.manaReservePercent)}% mana)`;

      // Click to toggle off
      el.addEventListener('click', () => {
        if (this.auraToggleCallback) {
          this.auraToggleCallback(a.skillId);
        }
      });

      this.elAuraBar.appendChild(el);
    }
  }

  // ---- Passive Point Badge ----

  private updatePassiveBadge(player: Player): void {
    const state = player.passiveTree.getState();
    const parts: string[] = [];
    if (state.branchPoints > 0) {
      parts.push(`+${state.branchPoints} Dal`);
    }
    if (state.availablePoints > 0) {
      parts.push(`+${state.availablePoints} Pasif`);
    }
    const text = parts.join(' | ');

    if (text !== this.prevPassiveBadgeText) {
      this.prevPassiveBadgeText = text;
      if (text) {
        this.elPassiveBadge.textContent = text;
        this.elPassiveBadge.className = 'hud-passive-badge hud-passive-badge--visible';
      } else {
        this.elPassiveBadge.textContent = '';
        this.elPassiveBadge.className = 'hud-passive-badge';
      }
    }
  }

  // ---- Combat Log ----

  private buildCombatLog(): HTMLDivElement {
    const el = document.createElement('div');
    el.className = 'hud-combat-log';
    this.elCombatLog = el;
    return el;
  }

  private updateCombatLog(deltaTime: number): void {
    for (let i = this.combatLogEntries.length - 1; i >= 0; i--) {
      const entry = this.combatLogEntries[i];
      entry.timer -= deltaTime;
      if (entry.timer <= 0.5 && !entry.el.classList.contains('hud-combat-entry--fade')) {
        entry.el.classList.add('hud-combat-entry--fade');
      }
      if (entry.timer <= 0) {
        entry.el.remove();
        this.combatLogEntries.splice(i, 1);
      }
    }
  }

  // ---- Pickup Notifications ----

  private buildPickups(): HTMLDivElement {
    const el = document.createElement('div');
    el.className = 'hud-pickups';
    this.elPickups = el;
    return el;
  }

  private updatePickups(deltaTime: number): void {
    for (let i = this.pickupEntries.length - 1; i >= 0; i--) {
      const entry = this.pickupEntries[i];
      entry.timer -= deltaTime;
      if (entry.timer <= 0.5 && !entry.el.classList.contains('hud-pickup--fade')) {
        entry.el.classList.add('hud-pickup--fade');
      }
      if (entry.timer <= 0) {
        entry.el.remove();
        this.pickupEntries.splice(i, 1);
      }
    }
  }

  // ---- Quest Complete Notification ----

  private buildQuestCompleteNotif(): HTMLDivElement {
    const wrap = document.createElement('div');
    wrap.className = 'hud-quest-complete';

    this.elQcTitle = document.createElement('div');
    this.elQcTitle.className = 'hud-qc-title';
    wrap.appendChild(this.elQcTitle);

    this.elQcName = document.createElement('div');
    this.elQcName.className = 'hud-qc-name';
    wrap.appendChild(this.elQcName);

    this.elQcRewards = document.createElement('div');
    this.elQcRewards.className = 'hud-qc-rewards';
    wrap.appendChild(this.elQcRewards);

    this.elQuestComplete = wrap;
    return wrap;
  }

  private updateQuestCompleteNotif(deltaTime: number): void {
    if (this.questCompleteTimer <= 0) return;

    this.questCompleteTimer -= deltaTime;

    if (this.questCompleteTimer <= 1.0 && !this.elQuestComplete.classList.contains('hud-quest-complete--fading')) {
      this.elQuestComplete.classList.add('hud-quest-complete--fading');
    }

    if (this.questCompleteTimer <= 0) {
      this.elQuestComplete.className = 'hud-quest-complete';
      this.questCompleteTimer = 0;
    }
  }
}
